import { deburr, fromPairs, groupBy, isNil, orderBy, toLower } from 'lodash'
import { truncateString } from 'common/utils/StringUtil'
import { PASTEL_COLOR } from 'common/utils/ColorUtil'
import { mainBlue } from 'app/theme'

// Source des lots du suivi : champ custom Mantis « Lot » (préfixes 0. à 4.) ou table Aquaparc parc_lots_dev.
export const SOURCE_MANTIS = 'mantis'
export const SOURCE_AQUAPARC = 'aquaparc'

export const ticketsLabel = (nb) => `${nb} ticket${nb > 1 ? 's' : ''}`

// Mélange d'une couleur hex avec du blanc : `strength` = part de couleur conservée (0 = blanc, 1 = couleur pure).
export const tintColor = (hex, strength) => {
    const mixed = [1, 3, 5].map(i => {
        const channel = parseInt(hex.slice(i, i + 2), 16)
        return Math.round(channel + (255 - channel) * (1 - strength)).toString(16).padStart(2, '0')
    })
    return `#${mixed.join('')}`
}

// Couleur d'accent d'un lot : terminé = vert, échéance dépassée = rouge, sinon bleu.
export const getLotHealthColor = (average, dueDate) => {
    if (average >= 100) {
        return PASTEL_COLOR.green
    }
    if (dueDate && dueDate * 1000 < Date.now()) {
        return PASTEL_COLOR.red
    }
    return mainBlue
}

export const LOT_CATEGORIE = { DEV: 0, SUPPORT: 1, ARBITRAGE: 2 }
export const LOT_STATUT = { EN_COURS: 1, A_VENIR: 2, ARCHIVE: 3 }

export const LOT_CATEGORIE_ITEMS = [
    { value: LOT_CATEGORIE.DEV, label: 'Développement' },
    { value: LOT_CATEGORIE.SUPPORT, label: 'Support' },
    { value: LOT_CATEGORIE.ARBITRAGE, label: 'Arbitrage' },
]

export const LOT_STATUT_ITEMS = [
    { value: LOT_STATUT.EN_COURS, label: 'En cours' },
    { value: LOT_STATUT.A_VENIR, label: 'À venir' },
    { value: LOT_STATUT.ARCHIVE, label: 'Archivé' },
]

export const LOT_GROUP = {
    CURRENTS: 'currentsTickets',
    TO_COME: 'toComeTickets',
    CLOSED: 'closedTickets',
    TO_ARBITRATE: 'toArbitrateTickets',
    SUPPORT: 'supportTickets',
    LOST: 'lostTickets',
}

const PREFIX_GROUPS = {
    0: LOT_GROUP.SUPPORT,
    1: LOT_GROUP.CURRENTS,
    2: LOT_GROUP.TO_COME,
    3: LOT_GROUP.CLOSED,
    4: LOT_GROUP.TO_ARBITRATE,
}

const STATUT_GROUPS = {
    [LOT_STATUT.EN_COURS]: LOT_GROUP.CURRENTS,
    [LOT_STATUT.A_VENIR]: LOT_GROUP.TO_COME,
    [LOT_STATUT.ARCHIVE]: LOT_GROUP.CLOSED,
}

const getGroupByPrefix = (lot) => PREFIX_GROUPS[lot?.match(/^([0-4])\./)?.[1]] ?? LOT_GROUP.LOST

const getGroupByDevLot = (devLot) => {
    if (isNil(devLot)) {
        return LOT_GROUP.LOST
    }
    if (devLot.categorie === LOT_CATEGORIE.SUPPORT) {
        return LOT_GROUP.SUPPORT
    }
    if (devLot.categorie === LOT_CATEGORIE.ARBITRAGE) {
        return LOT_GROUP.TO_ARBITRATE
    }
    return STATUT_GROUPS[devLot.statut] ?? LOT_GROUP.LOST
}

export const withMantisLots = (tickets) => tickets.map(t => ({ ...t, lotGroup: getGroupByPrefix(t.lot) }))

// En mode Aquaparc, l'appartenance vient de parc_lots_dev_tickets : le champ Mantis « Lot » du ticket est ignoré.
export const withDevLots = (tickets, devLots) => {
    const devLotByTicket = {}
    devLots.forEach(devLot => (devLot.tickets ?? []).forEach(idTicket => {
        devLotByTicket[idTicket] = devLot
    }))
    return tickets.map(t => {
        const lotRef = devLotByTicket[t.id]
        return { ...t, lot: lotRef?.nom, lotRef, lotGroup: getGroupByDevLot(lotRef) }
    })
}

const TICKET_SUMMARY_MAX_LENGTH = 60

// Propositions pour ajouter des tickets à un lot : les plus récents (id le plus haut) d'abord, sans ceux déjà dans
// le lot cible (targetLot, s'il est connu). Le lot actuel d'un ticket est indiqué, car 1 ticket = 1 lot :
// l'ajouter à un autre lot le déplace.
export const getTicketOptions = (tickets, targetLot) => {
    const candidates = isNil(targetLot) ? tickets : tickets.filter(t => t.lot !== targetLot)
    return orderBy(candidates, 'id', 'desc').map(t => {
        const currentLot = t.lot ? ` (${t.lot})` : ''
        return {
            id: t.id,
            label: `#${t.id} — ${truncateString(t.summary ?? '', TICKET_SUMMARY_MAX_LENGTH)}${currentLot}`,
            chipLabel: `#${t.id}`,
        }
    })
}

// Cible Aquaparc d'un préfixe du champ Mantis « Lot » : 0. support, 1. en cours, 2. à venir, 3. archivé, 4. arbitrage.
const PREFIX_TARGETS = {
    0: { categorie: LOT_CATEGORIE.SUPPORT },
    1: { categorie: LOT_CATEGORIE.DEV, statut: LOT_STATUT.EN_COURS },
    2: { categorie: LOT_CATEGORIE.DEV, statut: LOT_STATUT.A_VENIR },
    3: { categorie: LOT_CATEGORIE.DEV, statut: LOT_STATUT.ARCHIVE },
    4: { categorie: LOT_CATEGORIE.ARBITRAGE },
}

// Quand un même lot existe sous plusieurs préfixes, l'état le plus actif l'emporte (comme la reprise SQL).
const PREFIX_PRIORITY = ['1', '2', '3', '0', '4']

// Même règle que la reprise : valeur sans préfixe 0.-4. = lot « 3. » (archivé) dont le nom est la valeur entière.
const parseMantisLot = (value) => {
    const trimmed = (value ?? '').trim()
    if (!trimmed) {
        return undefined
    }
    const match = trimmed.match(/^([0-4])\.\s*(.+)$/)
    return match ? { prefix: match[1], nom: match[2].trim(), hasPrefix: true } : { prefix: '3', nom: trimmed, hasPrefix: false }
}

// Clé de comparaison d'un nom de lot : casse, accents et espaces multiples ignorés (comme la reprise SQL), mais PAS la
// ponctuation : « LOT_X » et « LOT_X-? » sont deux lots distincts.
const lotKey = (nom) => deburr(toLower(nom)).trim().replace(/\s+/g, ' ')

// Différences à appliquer pour aligner les lots Aquaparc sur le champ « Lot » de Mantis :
// - create : lot présent dans Mantis et absent d'Aquaparc (avec ses tickets) ;
// - state : catégorie / statut Aquaparc différent du préfixe Mantis ;
// - assign : tickets à affecter au lot Aquaparc (non affectés ou rangés dans un autre lot).
// `tickets` = [{ id, from }] où `from` est le lot Aquaparc actuel du ticket (absent si non affecté).
// `unprefixed` : tous les tickets du lot ont une valeur Mantis sans préfixe 0.-4. (lot traité comme archivé).
// Rien n'est retiré : un ticket sans lot dans Mantis n'est pas détaché côté Aquaparc.
export const computeLotsSync = (mantisTickets, devLots) => {
    const mantisRows = mantisTickets
        .map(t => ({ id: t.id, parsed: parseMantisLot(t.lot) }))
        .filter(({ parsed }) => !isNil(parsed))
    const mantisLots = Object.entries(groupBy(mantisRows, ({ parsed }) => lotKey(parsed.nom))).map(([key, rows]) => ({
        key,
        nom: rows[0].parsed.nom,
        prefixes: rows.map(({ parsed }) => parsed.prefix),
        unprefixed: rows.every(({ parsed }) => !parsed.hasPrefix),
        ticketIds: rows.map(({ id }) => id),
    }))

    // Plusieurs lots Aquaparc peuvent avoir la même clé (variantes de casse ou d'accents : « Évaluation », « evaluation »).
    const devLotsByKey = groupBy(devLots, devLot => lotKey(devLot.nom))
    const devLotByTicket = fromPairs(devLots.flatMap(devLot => (devLot.tickets ?? []).map(idTicket => [idTicket, devLot])))
    const withOrigin = (ids) => ids.map(id => ({ id, from: devLotByTicket[id]?.nom }))

    // Le lot Aquaparc retenu est celui qui contient déjà le plus de tickets du lot Mantis, à égalité celui au nom identique.
    const pickDevLot = (candidates = [], nom, ticketIds) => orderBy(
        candidates,
        [
            devLot => ticketIds.filter(id => devLotByTicket[id]?.id === devLot.id).length,
            devLot => devLot.nom === nom,
        ],
        ['desc', 'desc'],
    )[0]

    return mantisLots.flatMap(({ key, nom, prefixes, ticketIds, unprefixed }) => {
        const target = PREFIX_TARGETS[PREFIX_PRIORITY.find(p => prefixes.includes(p))]
        const devLot = pickDevLot(devLotsByKey[key], nom, ticketIds)
        if (isNil(devLot)) {
            return [{ id: `create-${key}`, type: 'create', unprefixed, nom, ...target, ticketIds, tickets: withOrigin(ticketIds) }]
        }
        const isStateDifferent = devLot.categorie !== target.categorie || (devLot.statut ?? undefined) !== target.statut
        const toAssign = ticketIds.filter(id => devLotByTicket[id]?.id !== devLot.id)
        return [
            ...(isStateDifferent ? [{ id: `state-${devLot.id}`, type: 'state', unprefixed, devLot, ...target }] : []),
            ...(toAssign.length ? [{ id: `assign-${devLot.id}`, type: 'assign', unprefixed, devLot, ticketIds: toAssign, tickets: withOrigin(toAssign) }] : []),
        ]
    })
}
